import { useState } from 'react'
import type { JSX } from 'react'
import {
  useParams,
  useNavigate,
  useResolvedPath,
  useLocation,
} from 'react-router-dom'

import { useQuery } from '@apollo/client/react'

import { Dialog } from '@buffer-mono/popcorn'
import { useSplitEnabled } from '@buffer-mono/features'

import { graphql, readFragment } from '~publish/graphql'
import { PostCard_Post } from '~publish/components/PostCard/fragments/PostCard_Post'
import { COLLABORATION_NOTES_SPLIT } from '~publish/components/PostDialogs/PostDetailsContent/useCollaborationNotesPanel'
import { PostUnreadMentionCount } from '~publish/hooks/notes/fragments/PostUnreadMentionCount'
import { PostUnreadNoteCount } from '~publish/hooks/notes/fragments/PostUnreadNoteCount'
import { isOptionalNotesSummaryError } from '~publish/hooks/notes/isOptionalNotesSummaryError'
import {
  NotesDismissGuardProvider,
  useNotesDismissGuard,
} from '~publish/hooks/notes/NotesDismissGuard'
import { useQueryParam } from '~publish/hooks/useQueryParam'

import { PostDetailsContent } from '../PostDetailsContent'
import { PostErrorDialog } from '../PostErrorDialog'
import { getReturnSearchOnDialogClose } from '../helpers'

import styles from '../PostDetailsContent/PostDetailsContent.module.css'

export const GetPost = graphql(
  `
    query GetPost($id: PostId!, $includeNotesSummary: Boolean! = false) {
      post(input: { id: $id }) {
        id
        ...PostCard_Post # TODO: Update this once the PostDetailsDialog has his own implementation
        ...PostUnreadNoteCount @include(if: $includeNotesSummary)
        ...PostUnreadMentionCount @include(if: $includeNotesSummary)
      }
    }
  `,
  [PostCard_Post, PostUnreadNoteCount, PostUnreadMentionCount],
)

/**
 * The single-post details dialog, mounted on the `/posts/:postId` (and
 * community `/post/:postId`) routes. This is the entry point for triggers
 * that can only express themselves as a URL — external deep links
 * (notifications, emails, shared links) and the app-shell composer toast.
 * In-app affordances open the group-aware PostGroupDialog in place instead.
 */
export function PostDetailsDialog(): JSX.Element | null {
  return (
    <NotesDismissGuardProvider>
      <PostDetailsDialogBody />
    </NotesDismissGuardProvider>
  )
}

function PostDetailsDialogBody(): JSX.Element | null {
  const resolvedPath = useResolvedPath('.')
  const navigate = useNavigate()
  const location = useLocation()
  const dismissGuard = useNotesDismissGuard()
  const { isReady, isEnabled } = useSplitEnabled(COLLABORATION_NOTES_SPLIT)

  const { postId, id: channelId } = useParams<{ postId: string; id: string }>()

  // Preserve the parent view's query state (date range, comparison, filters)
  // when navigating back, dropping only the dialog's own params.
  const returnSearch = getReturnSearchOnDialogClose(location.search)
  const [notesFocus] = useQueryParam('notesFocus')
  const [open, setOpen] = useState(true)
  const { data, loading, error } = useQuery(GetPost, {
    variables: {
      id: postId ?? '',
      includeNotesSummary: isReady && isEnabled,
    },
    errorPolicy: 'all',
  })

  const close = (): void => {
    setOpen(false)
    navigate(`..${returnSearch}`)
  }

  const onOpenChange = (nextOpen: boolean): void => {
    setOpen(nextOpen)
    if (!nextOpen) navigate(`..${returnSearch}`)
  }

  // The route still names this post by id after it's gone, so leave it for
  // the schedule list behind it rather than showing a "Post not found" error.
  const onPostDeleted = (): void => {
    setOpen(false)
    navigate(`..${returnSearch}`)
  }

  if (loading && !data) {
    return null
  }

  const rawPost = data?.post
  const post = readFragment(PostCard_Post, rawPost)
  if (channelId && post?.channel?.id && channelId !== post.channel.id) {
    // Correct the channel in the path but stay on the dialog route, so keep
    // the full search (we are not closing the dialog here).
    navigate(
      {
        pathname: resolvedPath.pathname.replace(channelId, post.channel.id),
        search: location.search,
      },
      { replace: true },
    )
  }

  if ((error && !isOptionalNotesSummaryError(error)) || !post || !rawPost) {
    return <PostErrorDialog error={error} />
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        size="large"
        // Override internal padding so we can set it manually
        style={{ padding: 0 }}
        onPointerDownOutside={dismissGuard.onPointerDownOutside}
        onEscapeKeyDown={dismissGuard.onEscapeKeyDown}
        onClickCapture={dismissGuard.onClickCapture}
        onClick={(event) => dismissGuard.onComposerClick(event, close)}
        className={styles.postDetailsDialog}
      >
        <PostDetailsContent
          post={rawPost}
          onPostDeleted={onPostDeleted}
          focusNotesComposer={notesFocus === 'true'}
        />
      </Dialog.Content>
    </Dialog>
  )
}
